import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Static } from "../../interfaces/Static";
import { getDropdownsByID } from "../../utils/Statics";
import { StaticTypes } from "../../enums/StaticTypes";
import { licenseState } from "../../state/QuoteState";
import { useRecoilState } from "recoil";
import Heading from "../../components/Heading";
import FormHeading from "../../components/FormHeading";
import CustomSelect from "../../components/CustomSelect";
import { LicenseType } from "../../interfaces/License";
import CustomDivider from "../../components/CustomDivider";
import DateSelector from "../../components/DateSelector";
import CustomRadio from "../../components/CustomRadio";
import { LicenseTypes } from "../../enums/LicenseTypes";

const License = ({
  setIsFormValid,
}: {
  setIsFormValid: (value: boolean) => void;
}) => {
  const { data: statics } = useQuery<Static[]>({ queryKey: ["statics"] });
  const [RSACodes, setRSACodes] = useState<Static[]>([]);

  const [license, setLicense] = useRecoilState(licenseState);

  const {
    day,
    licenseCode,
    licenseType,
    month,
    year,
    haveCurrentInsurance,
    coverClaimFreeYears: coverClaimFreeYear,
    theftClaimFreeYears: theftClaimFreeYear,
  } = license;

  const {
    licenseTypes,
    licenseCodes,
    theftClaimFreeYears,
    coverClaimFreeYears,
  } = useMemo(() => {
    return {
      licenseTypes: getDropdownsByID(StaticTypes.LICENSE_TYPES, statics || []),
      licenseCodes: getDropdownsByID(StaticTypes.LICENSE_CODES, statics || []),
      theftClaimFreeYears: getDropdownsByID(
        StaticTypes.ACCIDENT_CLAIM_FREE_YEARS,
        statics || []
      ).sort((a, b) => a.description.localeCompare(b.description)),
      coverClaimFreeYears: getDropdownsByID(
        StaticTypes.COVER_CLAIM_FREE_YEARS,
        statics || []
      ).sort((a, b) => a.description.localeCompare(b.description)),
    };
  }, [statics]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (licenseType !== "") {
      if (licenseType === LicenseTypes.FOREIGN) {
        setLicense({
          ...license,
          licenseCode: "FOREIGN DRIVER'S LICENCE (Motor)",
        });
      } else {
        if (licenseType === LicenseTypes.RSA) {
          setLicense({
            ...license,
            licenseCode: licenseCode,
          });
          let codes: Static[] = [];
          licenseCodes.forEach((lt: Static) => {
            if (
              ["B", "C1", "EC1", "TBA", "EB", "EC", "B", "C"].includes(lt.code)
            )
              codes.push(lt);
          });

          setRSACodes(codes);
        }

        if (licenseType === LicenseTypes.RSA_LEARNERS) {
          setLicense({
            ...license,
            licenseCode: "Learners License (Z)",
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseType]);

  useEffect(() => {
    if (haveCurrentInsurance)
      window.scrollBy({
        top: 400, // Scroll vertically by 'pixels'
        behavior: "smooth", // Optional: enables smooth scrolling
      });
  }, [haveCurrentInsurance]);

  useEffect(() => {
    setIsFormValid(
      licenseType !== "" &&
        licenseType !== "" &&
        day !== "" &&
        month !== "" &&
        year !== "" &&
        haveCurrentInsurance
        ? theftClaimFreeYear !== "" && coverClaimFreeYear !== ""
        : !!!haveCurrentInsurance
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    licenseCode,
    licenseType,
    day,
    month,
    year,
    haveCurrentInsurance,
    theftClaimFreeYear,
    coverClaimFreeYear,
  ]);

  const handleInputChange = (
    value: string | boolean,
    field: keyof LicenseType
  ) => {
    setLicense((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Heading tooltip="License details refer to the information on your driving license, such as your license type, motor vehicle code, and date of issue.">
        License
      </Heading>
      <FormHeading>License details</FormHeading>
      <CustomSelect
        items={licenseTypes}
        value={licenseType}
        placeholder="License type"
        onSelect={(value: string) => {
          handleInputChange(value, "licenseType");
          if (value !== LicenseTypes.RSA) {
            handleInputChange("", "licenseCode");
          }
        }}
        required
      />
      {licenseType === LicenseTypes.RSA && (
        <CustomSelect
          items={RSACodes}
          value={licenseCode}
          placeholder="License code"
          onSelect={(value: string) => {
            handleInputChange(value, "licenseCode");
          }}
          required
        />
      )}

      <CustomDivider />

      <FormHeading>Issue year</FormHeading>
      <DateSelector
        day={day}
        month={month}
        year={year}
        onDaySelect={(value) => {
          handleInputChange(value, "day");
        }}
        onMonthSelect={(value) => {
          handleInputChange(value, "month");
        }}
        onYearSelect={(value) => {
          handleInputChange(value, "year");
        }}
        required
      />

      <CustomDivider />

      <FormHeading>Insurance Details</FormHeading>
      <CustomRadio
        selected={haveCurrentInsurance}
        onSelect={(value: boolean) => {
          handleInputChange(value, "haveCurrentInsurance");
          handleInputChange("", "coverClaimFreeYears");
          handleInputChange("", "theftClaimFreeYears");
        }}
        name="currentInsurance"
        required
      >
        Do you currently have comprehensive vehicle insurance where you are
        noted as the regular driver?
      </CustomRadio>

      {haveCurrentInsurance && (
        <>
          <CustomSelect
            items={theftClaimFreeYears}
            value={theftClaimFreeYear}
            placeholder="Select"
            label="For how long have you had continuous comprehensive vehicle insurance
      for?"
            onSelect={(value: string) => {
              handleInputChange(value, "theftClaimFreeYears");
            }}
            required
          />
          <CustomSelect
            items={coverClaimFreeYears}
            value={coverClaimFreeYear}
            placeholder="Select"
            label="How many years has it been since you had your last vehicle accident or theft claim?"
            onSelect={(value: string) => {
              handleInputChange(value, "coverClaimFreeYears");
            }}
            required
          />
        </>
      )}
    </>
  );
};

export default License;
